import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Roi = () => {
  const [netProfit, setNetProfit] = useState("");
  const [investmentCost, setInvestmentCost] = useState("");
  const [roiValue, setRoiValue] = useState("0.00%");

  useEffect(() => {
    calculateROI();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [netProfit, investmentCost]);

  const calculateROI = () => {
    const netProfitValue = parseFloat(netProfit) || 0;
    const investmentCostValue = parseFloat(investmentCost) || 0;

    if (
      !isNaN(netProfitValue) &&
      !isNaN(investmentCostValue) &&
      investmentCostValue > 0
    ) {
      const roi =
        ((netProfitValue - investmentCostValue) / investmentCostValue) * 100;
      setRoiValue(roi.toFixed(2) + "%");
    } else {
      setRoiValue("0.00%");
    }
  };

  return (
    <Layout
      title="ROI(Return On Investment) Calculator"
      path="return-on-investment"
    >
      <section
        className="topics-detail-section section-padding"
        id="topics-detail"
      >
        <div className="container">
          <div className="row">
            {/* ROI Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  ROI (Return on Investment) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Net Profit */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="netProfit" className="form-label">
                        Net Profit (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Net Profit:</strong> The total profit after all expenses, taxes, and deductions have been subtracted from total revenue."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="netProfit"
                        step="0.01"
                        value={netProfit}
                        placeholder="Enter net profit"
                        onChange={(e) => setNetProfit(e.target.value)}
                      />
                    </div>

                    {/* Cost of Investment */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="investmentCost" className="form-label">
                        Cost of Investment (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Cost of Investment:</strong> The total amount of money invested to generate returns."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="investmentCost"
                        step="0.01"
                        value={investmentCost}
                        placeholder="Enter cost of investment"
                        onChange={(e) => setInvestmentCost(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        ROI: <span className="fw-bold ms-3">{roiValue}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="roi-introduction-section" id="roi-introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the ROI (Return on Investment) Calculator
                </h3>

                {/* What is an ROI Calculator? */}
                <h4 className="mt-3 mb-3">What is an ROI Calculator?</h4>
                <p>
                  An <strong>ROI (Return on Investment) Calculator</strong> is a
                  tool that helps measure the profitability of investments. It
                  factors in the net gains or losses relative to the cost of an
                  investment. By calculating the return on investment (ROI),
                  individuals and businesses can evaluate whether a particular
                  investment has been profitable or not.
                </p>

                {/* Why Use an ROI Calculator? */}
                <h4 className="mt-4 mb-3">Why Use an ROI Calculator?</h4>
                <p>Using an ROI calculator offers several advantages:</p>
                <ul>
                  <li>
                    <strong>Financial Planning:</strong> Helps plan and allocate
                    resources effectively by understanding the returns on
                    investments.
                  </li>
                  <li>
                    <strong>Investment Analysis:</strong> Provides a clear
                    analysis of how well an investment has performed.
                  </li>
                  <li>
                    <strong>Profitability Evaluation:</strong> Allows businesses
                    to identify profitable ventures or projects based on ROI.
                  </li>
                  <li>
                    <strong>Data-Driven Decisions:</strong> Helps in making
                    informed, data-backed investment decisions.
                  </li>
                </ul>

                {/* How to Use an ROI Calculator? */}
                <h4 className="mt-4 mb-3">How to Use an ROI Calculator?</h4>
                <p>To use an ROI calculator effectively, follow these steps:</p>
                <ul>
                  <li>
                    Enter the <strong>net profit</strong> from the investment.
                  </li>
                  <li>
                    Input the <strong>cost of investment</strong> made in the
                    project or venture.
                  </li>
                  <li>
                    The calculator will then compute the ROI based on the
                    entered values and display the result as a percentage.
                  </li>
                </ul>
                <p>
                  This allows users to determine how much profit (or loss) they
                  made relative to their investment, which is essential for
                  evaluating the effectiveness of the investment.
                </p>

                {/* What Factors Affect ROI Calculation? */}
                <h4 className="mt-4 mb-3">
                  What Factors Affect ROI Calculation?
                </h4>
                <p>
                  Several factors can affect the accuracy of the ROI
                  calculation:
                </p>
                <ul>
                  <li>
                    <strong>Net Profit:</strong> The actual earnings or losses
                    generated from the investment.
                  </li>
                  <li>
                    <strong>Cost of Investment:</strong> The initial amount
                    spent on the investment or project.
                  </li>
                  <li>
                    <strong>Time Period:</strong> The length of time the
                    investment was held can influence the ROI, especially for
                    long-term investments.
                  </li>
                  <li>
                    <strong>Additional Costs:</strong> Sometimes, hidden costs
                    or ongoing maintenance costs associated with the investment
                    can impact the ROI.
                  </li>
                </ul>

                {/* Who Should Use an ROI Calculator? */}
                <h4 className="mt-4 mb-3">Who Should Use an ROI Calculator?</h4>
                <p>An ROI calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Financial Firms:</strong> To assess the return on
                    investment of portfolios, stocks, bonds, and other financial
                    instruments.
                  </li>
                  <li>
                    <strong>Startups:</strong> To evaluate the success of new
                    projects, ventures, or business investments.
                  </li>
                  <li>
                    <strong>Businesses:</strong> To evaluate the effectiveness
                    of various projects, marketing campaigns, or capital
                    expenditures.
                  </li>
                  <li>
                    <strong>Investors:</strong> To track the performance of
                    personal or institutional investments and portfolios.
                  </li>
                </ul>

                {/* What Are the Benefits of Using an ROI Calculator? */}
                <h4 className="mt-4 mb-3">
                  What Are the Benefits of Using an ROI Calculator?
                </h4>
                <p>There are several benefits to using an ROI calculator:</p>
                <ul>
                  <li>
                    <strong>Clear Profitability Evaluation:</strong> Provides an
                    easy way to measure and assess the profitability of an
                    investment.
                  </li>
                  <li>
                    <strong>Better Investment Decisions:</strong> Helps
                    businesses and individuals choose the best investments based
                    on their returns.
                  </li>
                  <li>
                    <strong>Improved Financial Planning:</strong> Assists in
                    understanding how investments contribute to overall
                    financial goals and business growth.
                  </li>
                  <li>
                    <strong>Objective Insights:</strong> Offers a clear,
                    objective metric to compare multiple investments and make
                    informed decisions.
                  </li>
                </ul>

                <p>
                  In conclusion, the <strong>ROI Calculator</strong> is an
                  essential tool for measuring the effectiveness of investments.
                  It helps businesses, investors, and startups make data-driven
                  decisions by evaluating the return on investment and
                  identifying profitable ventures or projects. By using the ROI
                  calculator, users can assess their investment's success with
                  ease and precision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Roi;
